import { post, get } from '../utilities/fetch';
import forEach from '../utilities/forEach';
import setQueryStringParameter from '../utilities/setQueryStringParameter';
import dataLayerPush from '../utilities/dataLayerPush';
import { hideAllErrors } from '../taxi-and-limo/taxi-and-limo-search-box';
import { getPlaceFromAddress } from '../utilities/getPlaceFromAddress';
import formIntent from '../utilities/formIntent';

const FORM_SELECTOR = '[data-book-taxi-limo]';
const INPUT_SELECTOR = '[data-book-taxi-limo-address]';

const ERROR_MESSAGE_ACTIVE_STATE = 'error-message--active';

const DEFAULT_OPTIONS = {
	componentRestrictions: { country: ['ca', 'us'] }
};

export interface IApiResult {
	Address: string;
	ErrorCode: string;
	Fares: null | Fares[];
}

export interface Fares {
	BagCapacity: string;
	Category: string;
	Fare: string;
	PassengerCapacity: string;
	VehicleType: string;
}

export function main() {
	const forms = document.querySelectorAll<HTMLFormElement>(FORM_SELECTOR);

	if (forms.length === 0) {
		return;
	}

	forEach(forms, (form) => {
		const input = form.querySelector<HTMLInputElement>(INPUT_SELECTOR);
		const invalidInputErrorMessage = form.querySelector<HTMLElement>('[data-search-address-invalid]');
		let place: google.maps.places.PlaceResult;

		if (input) {
			// Initiate autocomplete
			const service = new google.maps.places.Autocomplete(input, DEFAULT_OPTIONS);

			// When a user clicks a address suggestion
			service.addListener('place_changed', () => {
				input.dispatchEvent(new Event('change'));

				place = service.getPlace();

				if (place.place_id) {
					toggleError(false, invalidInputErrorMessage!);

					onSubmitForm(form);
				}
			});

			// When a user submits the form
			form.addEventListener('submit', (e) => {
				e.preventDefault();

				onSubmitForm(form, place);
			});

			formIntent(form, () => {
				dataLayerPush({
					event: 'Taxi and limo search intent'
				});
			});
		}
	});
}

async function onSubmitForm(form: HTMLFormElement, place?: google.maps.places.PlaceResult) {
	const input = form.querySelector<HTMLInputElement>(INPUT_SELECTOR);

	const invalidInputErrorMessage = form.querySelector<HTMLElement>('[data-search-address-invalid]');

	if (input && input.value) {
		const potentialPlace = await getPlaceFromAddress(input.value);

		if (potentialPlace) {
			place = potentialPlace;
		}
	}

	// If a user did not select an address suggestion, 'place' could be undefined
	// If place is not undefined, submit the form through the fares API
	if (place && place.place_id) {
		hideAllErrors(form);
		toggleError(false, invalidInputErrorMessage!);

		const OPTIONS = {
			address: place.formatted_address,
			dataSourceId: form.getAttribute('data-source-id')
		};

		const api = form.getAttribute('data-api');

		if (api) {
			submitForm(form, api, OPTIONS);
		}
	} else {
		// Else show invalidInputErrorMessage
		hideAllErrors(form);
		toggleError(true, invalidInputErrorMessage!);

		dataLayerPush({
			event: 'Taxi and limo invalid address'
		});
	}
}

export async function submitForm(form: HTMLFormElement, api: string, OPTIONS) {
	const serviceNotAvailable = form.querySelector<HTMLElement>('[data-service-unavailable]')!;
	const noDataForAddress = form.querySelector<HTMLElement>('[data-no-data-for-address]')!;

	const button = form.querySelector<HTMLButtonElement>('.button');

	try {
		if (button) {
			showLoading(button);
		}

		dataLayerPush({
			event: 'Taxi and limo address form submit'
		});

		const response = await post(api, OPTIONS);
		const data: IApiResult = await response.json();

		hideAllErrors(form);

		if (data.ErrorCode === 'no.data.for.address') {
			toggleError(true, noDataForAddress);

			dataLayerPush({
				event: 'Taxi and limo out of range'
			});
		} else if (data.ErrorCode === 'service.unavailable') {
			toggleError(true, serviceNotAvailable);

			dataLayerPush({
				event: 'Taxi and limo service unavailable'
			});
		} else {
			// Submit
			onSuccessfulSubmit(form, data);
		}
	} catch (c) {
		toggleError(true, serviceNotAvailable);

		dataLayerPush({
			event: 'Taxi and limo service unavailable'
		});
	} finally {
		if (button) {
			hideLoading(button);
		}
	}
}

function onSuccessfulSubmit(form: HTMLFormElement, data: IApiResult) {
	const url = form.getAttribute('data-url');

	if (!url) {
		return;
	}

	// Set session storage
	const key = `Taxi and Limo ${data.Address}`;

	try {
		sessionStorage.setItem(key, JSON.stringify(data));
	} catch (e) {
		console.log('Error occured trying to set the session storage when getting quote data.');
	}

	const newUrl = setQueryStringParameter('address', data.Address, url);

	window.location.href = newUrl;
}

export function toggleError(display: boolean, errorMessage?: HTMLElement) {
	if (!errorMessage) {
		return;
	}

	if (display) {
		errorMessage.classList.add(ERROR_MESSAGE_ACTIVE_STATE);
		errorMessage.setAttribute('aria-hidden', 'false');
	} else {
		errorMessage.classList.remove(ERROR_MESSAGE_ACTIVE_STATE);
		errorMessage.setAttribute('aria-hidden', 'true');
	}
}

export function showLoading(button: HTMLButtonElement) {
	button.classList.add('button--loading');
	button.setAttribute('disabled', 'true');
}

export function hideLoading(button: HTMLButtonElement) {
	button.classList.remove('button--loading');
	button.removeAttribute('disabled');
}

main();
